import {UninitializedInitiationTypeError} from 'src/errors/initiation';
import {InvalidTrustedOriginError} from 'src/errors/origin';
import {TimeoutError} from 'src/errors/timeout';
import {
  HANDSHAKE_EVENTS,
  INITIATION_TYPES,
  type InitiationType,
  isInitiationMessage,
} from 'src/initiation';
import {BaseMessenger} from 'src/messengers/base';
import {GAUGE_EVENTS, INCREMENT_EVENTS} from 'src/metrics';
import {READY_EVENTS} from 'src/ready';
import {prepareSchema} from 'src/schema/prepared';
import {validate} from 'src/schema/validate';
import {targetContextFromMessagePort} from 'src/target/context/port';
import {TRUSTED_HOSTNAMES, TRUST_LEVELS} from 'src/trust-levels';
import {Deferred} from 'src/utils/deferred';
import {parseUrl} from 'src/utils/url';

import type {StandardSchemaV1} from '@standard-schema/spec';
import type {UNINITIALIZED_INITIATION_TYPE_ERROR} from 'src/errors/initiation';
import type {HandshakeEvent, InitiationMessage} from 'src/initiation';
import type {BaseMessengerOptions} from 'src/messengers/base';
import type {
  ManagedMessageData,
  ProcessSchema,
  UnsubscribeFn,
} from 'src/messengers/types';
import type {Message, TargetContext} from 'src/target/context';
import type {TrustLevel} from 'src/trust-levels';
import type {InferOutput} from 'src/types';

/**
 * Given a dictionary of schemas, process each schema based on the trust level.
 */
type ProcessSchemaByTypeEntries<
  TSchemaByTypeEntries extends Record<string, StandardSchemaV1>,
  TTrustLevel extends TrustLevel,
> = {
  [K in keyof TSchemaByTypeEntries]: ProcessSchema<
    TSchemaByTypeEntries[K],
    TTrustLevel
  >;
};

/**
 * establishConnection() is only available if the initiation type is set.
 */
type EstablishConnection<TOptions extends ManagedMessengerOptions> =
  TOptions['initiationType'] extends InitiationType
    ? () => Promise<void>
    : typeof UNINITIALIZED_INITIATION_TYPE_ERROR;

/**
 * The trust level of the remote target.
 */
export type TRemoteTrustLevel<TOptions extends ManagedMessengerOptions> =
  TOptions['remote']['trustLevel'] extends typeof TRUST_LEVELS.TRUSTED
    ? typeof TRUST_LEVELS.TRUSTED
    : typeof TRUST_LEVELS.UNTRUSTED;

/**
 * The trust level of the self target.
 */
export type TSelfTrustLevel<TOptions extends ManagedMessengerOptions> =
  TOptions['self']['trustLevel'] extends typeof TRUST_LEVELS.TRUSTED
    ? typeof TRUST_LEVELS.TRUSTED
    : typeof TRUST_LEVELS.UNTRUSTED;

/**
 * The schemaByType entries processed for the remote target.
 * This is used to validate the data sent to the remote target.
 * The schema is processed based on the trust level of the remote target.
 */
type TPreparedRemoteSchemaByTypeEntries<
  TOptions extends ManagedMessengerOptions,
> = ProcessSchemaByTypeEntries<
  TOptions['schemasByType'],
  TRemoteTrustLevel<TOptions>
>;
/**
 * The schemaByType entries processed for the self target.
 * This is used to validate the data received from the remote target.
 * The schema is processed based on the trust level of the self target.
 */
type TPreparedSelfSchemaByTypeEntries<
  TOptions extends ManagedMessengerOptions,
> = ProcessSchemaByTypeEntries<
  TOptions['schemasByType'],
  TSelfTrustLevel<TOptions>
>;

/**
 * The options for configuring the ManagedMessenger
 */
export interface ManagedMessengerOptions extends BaseMessengerOptions {
  /**
   * The schemas for each message type.
   */
  schemasByType: Record<string, StandardSchemaV1>;
  /**
   * The communication ID. Used to verify messages between messengers
   */
  communicationId?: string;

  /**
   * The initiation type. Used to determine the flow of the handshake.
   * This is only required if you want to establish a connection with the remote target with a private communication ID.
   * If you provide a communication ID, you don't need to establish a connection.
   */
  initiationType?: InitiationType;

  /**
   * Specifies if a message channel should be set up on initiation.
   * This adds an additional layer of security by ensuring that the communication is private and not exposed to other targets.
   * NOTE: You need to set `initiationType` and call establishConnection() to set this up.
   */
  setupChannelOnInitiation?: boolean;

  /**
   * The default timeout (in milliseconds) for all async operations in this messenger.
   * This is used to abort the operation if it takes too long.
   * @default 60 seconds
   */
  timeout?: number;

  /**
   * Specifis if localhost origins should be trusted by default.
   * This is a safer option to trust localhost origins used in development.
   */
  trustLocalhostOrigins?: boolean;

  /**
   * Specifies if the messenger should run the ready flow.
   * This is used to ensure that the remote target is ready before sending messages.
   * This is useful for targets that need to be initialized or take time to set up.
   */
  sendReadyEvents?: boolean;

  /**
   * Specifies origins that are allowed to be trusted.
   * If the remote trust level is set to TRUSTED, only Stripe origins are trusted by default.
   * You can add additional origins to be trusted by passing them here.
   * NOTE: This is UNSAFE because it can allow untrusted origins to be trusted.
   * Usage of this option should be avoided if possible, and will require security review.
   */
  UNSAFE_allowedTrustedOrigins?: string[];
}

/**
 * ManagedMessenger is the default recommended messenger to use for most cases.
 * It guarantees that the messages are sent and received in a secure manner and the sent data
 * is validated against the schemas provided.
 *
 * It requires that both the sender and receiver have the same schemas for each message type and
 * use the ManagedMessenger to send and receive messages. Therefore, it is not
 * suitable for use cases where the remote target is a third party that we don't control.
 *
 * Learn more at https://go/sail/comms/guides/managed-messenger
 */
export class ManagedMessenger<
  TOptions extends ManagedMessengerOptions,
> extends BaseMessenger {
  // prepare and cache schemas for each message type
  private preparedRemoteSchemasByType: TPreparedRemoteSchemaByTypeEntries<TOptions>;
  private preparedSelfSchemasByType: TPreparedSelfSchemaByTypeEntries<TOptions>;

  readonly establishConnection: EstablishConnection<TOptions>;

  /**
   * You probably want to use mustCommunicationId() instead of this.
   */
  private _communicationId?: string;
  protected remoteTrustLevel: TRemoteTrustLevel<TOptions>;
  protected selfTrustLevel: TSelfTrustLevel<TOptions>;
  private initiationType?: InitiationType;
  private setupChannelOnInitiation?: boolean;
  private establishedPortContext?: TargetContext;
  protected messengerType = 'managed';
  protected timeout: number;
  private sendReadyEvents = false;
  private readyDeferred = new Deferred({
    signal: this.controller.signal,
  });

  constructor(options: TOptions) {
    validateOptions(options);

    super(options);
    this._communicationId = options.communicationId;
    this.remoteTrustLevel =
      (options.remote.trustLevel as TRemoteTrustLevel<TOptions>) ??
      TRUST_LEVELS.UNTRUSTED;
    this.selfTrustLevel =
      (options.self.trustLevel as TSelfTrustLevel<TOptions>) ??
      TRUST_LEVELS.UNTRUSTED;
    this.initiationType = options.initiationType;
    this.setupChannelOnInitiation = options.setupChannelOnInitiation;
    this.timeout = options.timeout ?? 60 * 1000; // default to 60 seconds
    this.sendReadyEvents = options.sendReadyEvents ?? false;

    this.preparedRemoteSchemasByType =
      {} as TPreparedRemoteSchemaByTypeEntries<TOptions>;
    // TODO(imolorhe): Try to figure out the types here and remove the assertion
    Object.keys(options.schemasByType).forEach(
      <K extends keyof TPreparedRemoteSchemaByTypeEntries<TOptions> & string>(
        type: K,
      ) => {
        const schema = options.schemasByType[type];
        const {allsSchema, nonsensitiveOnlySchema} = prepareSchema(schema);
        const prepared = (
          this.remoteTrustLevel === TRUST_LEVELS.UNTRUSTED
            ? nonsensitiveOnlySchema
            : allsSchema
        ) as TPreparedRemoteSchemaByTypeEntries<TOptions>[typeof type];

        this.preparedRemoteSchemasByType[type] = prepared;
      },
    );

    this.preparedSelfSchemasByType =
      {} as TPreparedSelfSchemaByTypeEntries<TOptions>;
    // TODO(imolorhe): Try to figure out the types here and remove the assertion
    Object.keys(options.schemasByType).forEach(
      <K extends keyof TPreparedSelfSchemaByTypeEntries<TOptions> & string>(
        type: K,
      ) => {
        const schema = options.schemasByType[type];
        const {allsSchema, nonsensitiveOnlySchema} = prepareSchema(schema);
        const prepared = (
          this.selfTrustLevel === TRUST_LEVELS.UNTRUSTED
            ? nonsensitiveOnlySchema
            : allsSchema
        ) as TPreparedSelfSchemaByTypeEntries<TOptions>[typeof type];

        this.preparedSelfSchemasByType[type] = prepared;
      },
    );

    // Using this to provide feedback to the user via type errors if they try to
    // call establishConnection() without providing an initiation type
    this.establishConnection = (() => {
      return this.establishConnectionImpl();
    }) as EstablishConnection<TOptions>;

    if (this.sendReadyEvents) {
      this.manageReadyState();
    }
  }

  /**
   * Perform handshake with the remote target to establish a connection.
   * This is required to set up the communication ID if it is not provided.
   * The handshake will be performed using the initiation type provided in the options.
   */
  private async establishConnectionImpl(): Promise<void> {
    if (!this.initiationType) {
      throw new UninitializedInitiationTypeError();
    }
    /**
     * Handshake process:
     * initiator -- INIT{initiatorNonce} --> responder
     * responder -- INIT_ACK{initiatorNonce, responderNonce} --> initiator
     * initiator -- ACK{initiatorNonce, responderNonce, communicationId, port} --> responder
     */
    const start = performance.now();
    try {
      if (this._communicationId) {
        this.debug(
          'Communication ID already set. No need to establish connection',
        );
        return;
      }

      this.debug('Establishing connection with remote target');
      switch (this.initiationType) {
        case INITIATION_TYPES.INITIATOR: {
          await this.establishConnectionForInitiator();
          break;
        }
        case INITIATION_TYPES.RESPONDER: {
          await this.establishConnectionForResponder();
        }
      }
    } catch (e) {
      if (e instanceof TimeoutError) {
        this.increment(INCREMENT_EVENTS.TIMEOUT_ERROR);
      }
      this.increment(INCREMENT_EVENTS.HANDSHAKE_ERROR);

      this.error('Error while establishing connection', e);
      throw e;
    } finally {
      this.gauge(GAUGE_EVENTS.HANDSHAKE_DURATION_MS, performance.now() - start);
    }
  }

  private async establishConnectionForInitiator(): Promise<void> {
    const done = new Deferred({
      timeoutMs: this.timeout,
      signal: this.controller.signal,
    });
    const listener = (
      message: Message<InitiationMessage<HandshakeEvent>>,
    ): void => {
      try {
        if (!isInitiationMessage(message.data)) {
          this.warn('Invalid initiation message received. Not calling back');
          return;
        }
        this.debug('📥 received initiation message', message.data);

        switch (message.data.$__event) {
          case HANDSHAKE_EVENTS.INIT_ACK: {
            if (message.data.$__i_nonce !== this.id) {
              this.warn(
                'Invalid init ack message received. i_nonce does not match',
              );
              return;
            }

            if (!message.data.$__r_nonce) {
              this.warn(
                'Invalid init ack message received. No r_nonce provided',
              );
              return;
            }
            this.debug(
              'Received valid init ack from responder. Setting up communication ID',
            );
            const communicationId = this.generateId();
            this._communicationId = communicationId;
            let port: MessagePort | undefined;
            let transferables: Transferable[] | undefined;

            if (this.setupChannelOnInitiation) {
              this.debug('Setting up message channel');
              const messageChannel = new MessageChannel();
              const {port1: initiatorPort, port2: responderPort} =
                messageChannel;
              port = initiatorPort;
              transferables = [responderPort];
            }

            this.sendRawMessage<InitiationMessage>(
              {
                $__event: HANDSHAKE_EVENTS.ACK,
                $__communicationId: communicationId,
                $__i_nonce: this.id,
                $__r_nonce: message.data.$__r_nonce,
                $__v: '1',
              },
              transferables,
            );

            if (port) {
              this.debug('Starting message port');
              port.start();
              this.establishedPortContext = targetContextFromMessagePort(port);
            }

            this.removeMessageEventListener(listener);
            this.debug(
              'Handshake complete. Communication ID set up',
              this._communicationId,
              'and listener removed',
            );
            done.resolve();
            break;
          }
        }
      } catch (e) {
        done.reject(e);
      }
    };
    this.addMessageEventListener<InitiationMessage>(listener);
    const initMessage: InitiationMessage = {
      $__event: HANDSHAKE_EVENTS.INIT,
      $__i_nonce: this.id,
      $__v: '1',
    };
    this.sendRawMessage(initMessage);

    try {
      await done.promise;
    } finally {
      this.removeMessageEventListener(listener);
    }
  }

  private async establishConnectionForResponder(): Promise<void> {
    const done = new Deferred({
      timeoutMs: this.timeout,
      signal: this.controller.signal,
    });
    let initiatorNonce: string | undefined;
    const listener = (
      message: Message<InitiationMessage<HandshakeEvent>>,
    ): void => {
      try {
        if (!isInitiationMessage(message.data)) {
          this.warn('Invalid initiation message received. Not calling back');
          return;
        }
        this.debug('📥 received initiation message', message.data);

        switch (message.data.$__event) {
          case HANDSHAKE_EVENTS.INIT: {
            if (!message.data.$__i_nonce) {
              this.warn('Invalid init message received. No i_nonce provided');
              return;
            }
            initiatorNonce = message.data.$__i_nonce;
            this.debug(
              'Received valid init message from initiator. Sending init ack',
            );
            this.sendRawMessage<InitiationMessage>({
              $__event: HANDSHAKE_EVENTS.INIT_ACK,
              $__i_nonce: message.data.$__i_nonce,
              $__r_nonce: this.id,
              $__v: '1',
            });
            break;
          }
          case HANDSHAKE_EVENTS.ACK: {
            if (!initiatorNonce) {
              this.warn(
                'Invalid ack message received. init message not yet received',
              );
              return;
            }
            if (message.data.$__i_nonce !== initiatorNonce) {
              this.warn('Invalid ack message received. i_nonce does not match');
              return;
            }
            if (message.data.$__r_nonce !== this.id) {
              this.warn('Invalid ack message received. r_nonce does not match');
              return;
            }
            if (!message.data.$__communicationId) {
              this.warn(
                'Invalid ack message received. No communication ID provided',
              );
              return;
            }
            this.debug(
              'Received valid ack from initiator. Setting up communication ID',
            );
            this._communicationId = message.data.$__communicationId;

            if (this.setupChannelOnInitiation) {
              this.debug('Using message port from the initiator');
              const port = message.ports?.[0];
              if (!port) {
                throw new Error(
                  'No message port provided in the ack message. Make sure the initiator also has setupChannelOnInitiation set to true',
                );
              }
              port.start();
              this.establishedPortContext = targetContextFromMessagePort(port);
            }

            this.removeMessageEventListener(listener);
            this.debug(
              'Handshake complete. Communication ID set up',
              this._communicationId,
              'and listener removed',
            );
            this.increment(INCREMENT_EVENTS.HANDSHAKE_COMPLETE);
            done.resolve();
            break;
          }
        }
      } catch (e) {
        done.reject(e);
      }
    };
    this.addMessageEventListener<InitiationMessage>(listener);

    try {
      await done.promise;
    } finally {
      this.removeMessageEventListener(listener);
    }
  }

  /**
   * Check if the messenger is ready.
   * This is true if we have received the ready message from the remote target.
   */
  isReady(): boolean {
    if (!this.sendReadyEvents) {
      throw new Error(
        'Ready events are not enabled. Please enable sendReadyEvents in the options to use this method',
      );
    }
    return this.readyDeferred.isResolved();
  }

  /**
   * Wait for the messenger to be ready.
   * This will wait for the ready message from the remote target.
   * If the messenger is already ready, this will resolve immediately.
   */
  async waitForReady(): Promise<void> {
    if (!this.sendReadyEvents) {
      throw new Error(
        'Ready events are not enabled. Please enable sendReadyEvents in the options to use this method',
      );
    }
    // if the messenger is already ready, resolve immediately
    if (this.isReady()) {
      return Promise.resolve();
    }
    await this.readyDeferred.promise;
  }

  // this is called in the constructor, and should not be async to handle errors better
  private manageReadyState(): void {
    const listener = (message: Message<unknown>): void => {
      try {
        switch (message.data) {
          case READY_EVENTS.READY: {
            this.removeMessageEventListener(listener);
            this.debug('Remote is ready.');
            // send ready ack message
            this.sendRawMessage(READY_EVENTS.READY_ACK);
            // set ready state
            this.readyDeferred.resolve();
            return;
          }
          case READY_EVENTS.READY_ACK: {
            this.removeMessageEventListener(listener);
            this.debug('Remote acked ready.');
            // set ready state
            this.readyDeferred.resolve();
          }
        }
      } catch (e) {
        this.error('Error managing ready state', e);
      }
    };
    this.addMessageEventListener(listener);
    // send ready message
    this.sendRawMessage(READY_EVENTS.READY);
    this.debug('Sent ready message');
  }

  /**
   * Send a valid message to the remote target based on the schema provided for the message type.
   */
  sendMessage<
    TType extends keyof TPreparedRemoteSchemaByTypeEntries<TOptions>,
    TProcessedSchema extends TPreparedRemoteSchemaByTypeEntries<TOptions>[TType] = TPreparedRemoteSchemaByTypeEntries<TOptions>[TType],
  >(
    type: TType,
    data: InferOutput<TProcessedSchema>,
    transfer?: Transferable[],
  ): void {
    const start = performance.now();
    // validate the data against the schema
    const validData = this.validateDataForRemote(type, data);

    if (!validData) {
      this.increment(INCREMENT_EVENTS.OUTPUT_VALIDATION_FAILED);
      this.error('Validation failed. Not sending message');
      return;
    }

    this.debug('Validated message. Sending..');
    this.gauge(
      GAUGE_EVENTS.OUTPUT_VALIDATION_DURATION_MS,
      performance.now() - start,
    );
    const managedData: ManagedMessageData<
      TType,
      InferOutput<TProcessedSchema>
    > = {
      $__type: type,
      $__payload: validData,
      $__communicationId: this.mustCommunicationId(),
      $__v: '1',
    };

    this.sendRawMessage(managedData, transfer);
  }

  /**
   * Listen for messages from the remote target. The callback will be called
   * every time a valid message is received. To stop listening, call the returned unsubscribe function.
   * The message will be validated against the schemas provided in `schemasByType`.
   */
  onMessage<
    TType extends keyof TPreparedSelfSchemaByTypeEntries<TOptions>,
    TProcessedSchema extends TPreparedSelfSchemaByTypeEntries<TOptions>[TType] = TPreparedSelfSchemaByTypeEntries<TOptions>[TType],
  >(
    callback: (data: InferOutput<TProcessedSchema>) => void,
    opts?: {
      isValid?: (
        data: InferOutput<TProcessedSchema>,
        managedData: ManagedMessageData<TType, InferOutput<TProcessedSchema>>,
      ) => boolean;
    },
  ): UnsubscribeFn {
    this.debug('👂 listening..');
    const wrappedListener = (
      message: Message<
        ManagedMessageData<TType, InferOutput<TProcessedSchema>>
      >,
    ): void => {
      this.debug('📥 received message');
      const inputValidationStart = performance.now();
      const eventData = message.data;
      // validate managed message data
      if (!this.isValidManagedMessageData(eventData)) {
        this.error(
          'Invalid managed message data received. Not calling callback',
        );
        this.increment(INCREMENT_EVENTS.INPUT_VALIDATION_FAILED);
        return;
      }

      // validate managed message communication ID
      if (eventData.$__communicationId !== this.mustCommunicationId()) {
        this.error(
          `Invalid communication ID received. Expected: ${this.mustCommunicationId()}. Received: ${
            eventData.$__communicationId
          }`,
        );
        this.increment(INCREMENT_EVENTS.COMM_ID_VERIFICATION_FAILED);
        return;
      }

      const validData = this.validateDataForSelf(
        eventData.$__type,
        eventData.$__payload,
      );
      if (!validData) {
        this.error('Validation failed. Not calling callback');
        this.increment(INCREMENT_EVENTS.INPUT_VALIDATION_FAILED);
        return;
      }

      if (opts?.isValid && !opts.isValid(validData, eventData)) {
        this.error('Custom validation failed. Not calling callback');
        return;
      }
      this.gauge(
        GAUGE_EVENTS.INPUT_VALIDATION_DURATION_MS,
        performance.now() - inputValidationStart,
      );
      this.increment(INCREMENT_EVENTS.ON_MESSAGE_RECEIVED);
      this.debug('Validated message. Calling callback..');
      callback(validData);
    };

    this.addMessageEventListener<
      ManagedMessageData<TType, InferOutput<TProcessedSchema>>
    >(wrappedListener);

    return () => {
      this.debug('🙉 stopped listening');
      this.removeMessageEventListener(wrappedListener);
    };
  }

  /**
   * Listen for messages from the remote target based on the message type.
   * The callback will be called every time a valid message for the specified type is received.
   * To stop listening, call the returned unsubscribe function.
   */
  onMessageType<TType extends keyof TPreparedSelfSchemaByTypeEntries<TOptions>>(
    type: TType,
    callback: (
      data: InferOutput<TPreparedSelfSchemaByTypeEntries<TOptions>[TType]>,
    ) => void,
  ): UnsubscribeFn {
    return this.onMessage<TType>(callback, {
      isValid: (_, managedData) => {
        // validate message type
        const isValidType = managedData.$__type === type;
        if (!isValidType) {
          this.error(
            `Invalid message type received. Expected: ${String(
              type,
            )}. Received: ${String(managedData.$__type)}`,
          );
        }
        return isValidType;
      },
    });
  }

  /**
   * Listen for a single message with the message type from the remote target and unsubscribe automatically after receiving it.
   * The callback will be called when a valid message for the specified type is received.
   * You can also stop listening anytime by calling the returned unsubscribe function.
   */
  onceMessageType<
    TType extends keyof TPreparedSelfSchemaByTypeEntries<TOptions>,
  >(
    type: TType,
    callback: (
      data: InferOutput<TPreparedSelfSchemaByTypeEntries<TOptions>[TType]>,
    ) => void,
  ): UnsubscribeFn {
    this.debug('👂 listening for a single message');
    const unsub = this.onMessageType(type, (data) => {
      this.increment(INCREMENT_EVENTS.ONCE_MESSAGE_RECEIVED);
      callback(data);
      unsub();
    });
    return () => {
      this.debug('🙉 stopped listening for once message');
      unsub();
    };
  }

  protected override remoteContext(): TargetContext {
    // use the context from the private message channel if that has been setup
    return this.establishedPortContext ?? super.remoteContext();
  }

  protected override selfContext(): TargetContext {
    // use the context from the private message channel if that has been setup
    return this.establishedPortContext ?? super.selfContext();
  }

  protected mustCommunicationId(): string {
    if (!this._communicationId) {
      throw new Error(
        'Communication ID is required. Please provide a communication ID when creating the messenger or call establishConnection() to set it up before sending messages.',
      );
    }
    return this._communicationId;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private validateDataForRemote<
    TType extends keyof TPreparedRemoteSchemaByTypeEntries<TOptions>,
    TProcessedSchema extends TPreparedRemoteSchemaByTypeEntries<TOptions>[TType],
  >(type: TType, data: InferOutput<TProcessedSchema>) {
    const preparedSchema = this.preparedRemoteSchemasByType[type];
    if (!preparedSchema) {
      this.error(`No prepared schema found for message type: ${String(type)}`);
      return;
    }

    return validate(preparedSchema, data);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private validateDataForSelf<
    TType extends keyof TPreparedSelfSchemaByTypeEntries<TOptions>,
    TProcessedSchema extends TPreparedSelfSchemaByTypeEntries<TOptions>[TType],
  >(type: TType, data: InferOutput<TProcessedSchema>) {
    const preparedSchema = this.preparedSelfSchemasByType[type];
    if (!preparedSchema) {
      this.error(`No prepared schema found for message type: ${String(type)}`);
      return;
    }

    return validate(preparedSchema, data);
  }

  private isValidManagedMessageData<TType, TData>(
    data: unknown,
  ): data is ManagedMessageData<TType, TData> {
    const _ = data as ManagedMessageData<TType, TData>;
    return !!(
      typeof data === 'object' &&
      data !== null &&
      _.$__type &&
      _.$__payload &&
      _.$__communicationId &&
      _.$__v === '1' &&
      _.$__communicationId === this.mustCommunicationId()
    );
  }
}

function validateOptions(options: ManagedMessengerOptions): void {
  if (
    options.remote.trustLevel &&
    !Object.values(TRUST_LEVELS).includes(options.remote.trustLevel)
  ) {
    throw new Error(`Invalid trust level: ${options.remote.trustLevel}`);
  }

  if (options.remote.trustLevel === TRUST_LEVELS.TRUSTED) {
    if (
      options.remote.origin &&
      !isTrustedOrigin(
        options.remote.origin,
        options.UNSAFE_allowedTrustedOrigins,
        options.trustLocalhostOrigins,
      )
    ) {
      throw new InvalidTrustedOriginError(options.remote.origin);
    }
  }
}

function isTrustedOrigin(
  origin: string,
  additionalTrustedOrigins?: string[],
  trustLocalhostOrigins?: boolean,
): boolean {
  if (additionalTrustedOrigins && additionalTrustedOrigins.includes(origin)) {
    return true;
  }

  const u = parseUrl(origin);
  if (!u) {
    return false;
  }
  // if the origin is the same as the current origin, we can consider it as trusted
  // i.e. any data we have access to, the remote target also has access to it
  if (u.origin === location.origin) {
    return true;
  }

  // allow localhost origins if trustLocalhostOrigins is set
  if (trustLocalhostOrigins && u.hostname === 'localhost') {
    return true;
  }

  // allow trusted Stripe origins
  const isTrustedHostname = TRUSTED_HOSTNAMES.find((trustedHostname) => {
    if (
      u.hostname === trustedHostname ||
      u.hostname.endsWith(`.${trustedHostname}`)
    ) {
      return true;
    }

    return false;
  });

  if (isTrustedHostname) {
    return true;
  }

  return false;
}
